/**
 * **一次造多个词**（2026-10-30 用户要求）。
 *
 * ## 用户原话
 *
 * > "这个造词功能有点不方便，每次只能造一个词，可不可以一次造多个词？
 * >  然后还要选这词是词语还是成语，你要自动识别造的词是词语还是成语。"
 *
 * ## 它做什么
 *
 * 1. 一个多行输入框：换行 / 空格 / 逗号 / 顿号都能分（`lib/wordBatch.ts`，有单测）；
 * 2. **自动判断类别**：4 个字及以上算成语、2～3 个字算词语 —— 不用再手选；
 * 3. 先预览：每条显示「词 + 自动类别」，不合法的当场标红并说明原因；
 * 4. 一次提交：逐条创建，成功的进列表、失败的**逐条**报原因（不整批回滚 ——
 *    家长输错一个字不该让另外九个白填）；
 * 5. 每个新词都把这个字**钉在这个读音上**（多音字按音造词，与原单个造词的流程一致）。
 *
 * ⚠️ 这里**只造"自己造的词"**（`fromTextbook: false`）：课本词语表是导入的事实，
 *    走「整块粘贴 / 导入」那条路，不从这儿进。
 */
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Card';
import { toErrorMessage } from '../../lib/api/error';
import {
  createWord,
  setWordCharReadings,
  type CreateWordPayload,
} from '../../lib/api/dictationAdmin';
import { parseWordBatch } from '../../lib/wordBatch';
import type { HanziView } from '../../lib/api/dictation';
import { cn } from '../../lib/utils';

export interface WordBatchCreateModalProps {
  open: boolean;
  /** 造的是**哪个字**的词（要用它的课本 / 年级 / 学期 / 字面 / 读音）。 */
  hanzi: HanziView | null;
  /** 这些词里「这个字」读哪个音（多音字按音造词）。 */
  reading: string;
  onClose: () => void;
  /** 全部处理完后调用（页面据此刷新浮窗与列表）。 */
  onSaved: () => void | Promise<void>;
}

export function WordBatchCreateModal({
  open,
  hanzi,
  reading,
  onClose,
  onSaved,
}: WordBatchCreateModalProps) {
  const [raw, setRaw] = useState('');
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState<{ text: string; message: string }[]>([]);

  /** 每次打开都清空（避免上次输到一半的内容出现在下一次）。 */
  useEffect(() => {
    if (open) {
      setRaw('');
      setFailures([]);
      setProgress(null);
    }
  }, [open]);

  const items = useMemo(() => parseWordBatch(raw), [raw]);
  const valid = items.filter((item) => item.error === null);
  const invalid = items.filter((item) => item.error !== null);

  const save = async () => {
    if (hanzi === null || valid.length === 0 || saving) return;
    setSaving(true);
    setFailures([]);
    setProgress({ done: 0, total: valid.length });
    const failed: { text: string; message: string }[] = [];
    let done = 0;

    for (const item of valid) {
      try {
        const payload: CreateWordPayload = {
          textbookId: hanzi.textbookId,
          wordText: item.text,
          category: item.category,
          fromTextbook: false,
          gradeCode: hanzi.gradeCode,
          semester: hanzi.semester,
        };
        const word = await createWord(payload);
        /**
         * 把这个字**钉在这个读音上**（与单个造词的流程一致：其它位置传空串 =
         * 从整词拼音推，不替用户猜）。
         */
        const chars = [...word.wordText];
        if (chars.includes(hanzi.charText)) {
          await setWordCharReadings(
            word.id,
            chars.map((ch) => (ch === hanzi.charText ? reading : '')),
          );
        }
      } catch (err) {
        failed.push({ text: item.text, message: toErrorMessage(err) });
      }
      done += 1;
      setProgress({ done, total: valid.length });
    }

    setSaving(false);
    setProgress(null);
    setFailures(failed);
    if (failed.length === 0) {
      await onSaved();
      onClose();
    } else {
      /** 有失败的：留在窗口里逐条报，成功的已经从输入框里去掉。 */
      setRaw(failed.map((item) => item.text).join('\n'));
      await onSaved();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={hanzi === null ? '批量造词' : `给「${hanzi.charText}」一次造多个词（读 ${reading}）`}
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          每行一个词，也可以用空格、逗号分开。
          <br />
          <span className="text-slate-500">
            类别自动判断：<strong>4 个字及以上算成语</strong>，2～3 个字算词语。
          </span>
        </p>

        <textarea
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          rows={6}
          disabled={saving}
          placeholder={'三言两语\n半斤八两\n七上八下'}
          className={cn(
            'w-full rounded-xl border border-slate-300 px-3 py-2 text-sm',
            'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
          )}
        />

        {items.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              识别到 {items.length} 个词
              {invalid.length > 0 ? `，其中 ${invalid.length} 个不能提交` : ''}
            </p>
            <ul className="flex flex-wrap gap-1.5">
              {items.map((item) => (
                <li
                  key={item.text}
                  className={cn(
                    'flex items-center gap-1 rounded-lg px-2 py-0.5 text-sm',
                    item.error === null ? 'bg-slate-100 text-slate-800' : 'bg-rose-50 text-rose-700',
                  )}
                  title={item.error ?? `将作为${item.category === 'idiom' ? '成语' : '词语'}保存`}
                >
                  <span>{item.text}</span>
                  <span className="text-[11px] text-slate-500">
                    {item.category === 'idiom' ? '成语' : '词语'}
                  </span>
                  {item.error !== null && <span className="text-[11px]">（{item.error}）</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {failures.length > 0 && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
            <p className="text-sm font-medium text-rose-800">有 {failures.length} 个没存上：</p>
            <ul className="mt-1 space-y-0.5 text-xs text-rose-700">
              {failures.map((item) => (
                <li key={item.text}>
                  「{item.text}」：{item.message}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-rose-600">
              这 {failures.length} 个已经留在上面的输入框里，改完可以再点保存。
            </p>
          </div>
        )}

        {progress !== null && (
          <p className="text-xs text-slate-500">
            正在保存 {progress.done} / {progress.total}…
          </p>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {valid.length > 0 && (
            <Badge tone="neutral">将保存 {valid.length} 个</Badge>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={valid.length === 0}>
            保存 {valid.length > 0 ? `${valid.length} 个` : ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
